import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import SportsIcon from '@mui/icons-material/Sports';
import api from '../services/api';

const GSI_SRC = 'https://accounts.google.com/gsi/client';
const FONT = "'Noto Sans TC', -apple-system, 'Microsoft JhengHei', sans-serif";

// 載入 Google Identity Services script（只載一次）
function loadGsiScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

// 排行榜長條意象：金銀銅與其後名次
const BARS = [
  { h: 46, c: 'rgba(255,255,255,0.28)' },
  { h: 64, c: 'rgba(255,255,255,0.38)' },
  { h: 100, c: '#f5b301' },
  { h: 82, c: 'rgba(255,255,255,0.55)' },
  { h: 58, c: 'rgba(255,255,255,0.34)' },
  { h: 38, c: 'rgba(255,255,255,0.24)' },
  { h: 28, c: 'rgba(255,255,255,0.18)' },
];

function Leaderboard() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1.5,
        height: { xs: 72, md: 140 },
        '& > span': {
          flex: 1,
          maxWidth: 44,
          borderRadius: '6px 6px 0 0',
          transformOrigin: 'bottom',
          animation: 'bar-rise 600ms cubic-bezier(0.22, 1, 0.36, 1) both',
        },
        '@keyframes bar-rise': {
          from: { transform: 'scaleY(0)' },
          to: { transform: 'scaleY(1)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& > span': { animation: 'none' },
        },
      }}
    >
      {BARS.map((bar, i) => (
        <Box
          key={i}
          component="span"
          sx={{
            height: `${bar.h}%`,
            bgcolor: bar.c,
            animationDelay: `${i * 70}ms !important`,
          }}
        />
      ))}
    </Box>
  );
}

function LoginPage({ onLogin }) {
  const buttonRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const [{ data: config }] = await Promise.all([
          api.get('/auth/config'),
          loadGsiScript(),
        ]);
        if (cancelled) return;

        if (!config.client_id) {
          setError('伺服器尚未設定 Google Client ID，請聯絡管理員');
          setLoading(false);
          return;
        }

        window.google.accounts.id.initialize({
          client_id: config.client_id,
          callback: async (response) => {
            try {
              const { data } = await api.post('/auth/login', {
                credential: response.credential,
              });
              onLogin(data.token, { email: data.email, name: data.name });
            } catch (err) {
              setError(
                err.response?.data?.message || 'Google 登入失敗，請再試一次'
              );
            }
          },
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'signin_with',
          logo_alignment: 'center',
        });
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError('登入服務載入失敗，請重新整理頁面');
          setLoading(false);
        }
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [onLogin]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        fontFamily: FONT,
        bgcolor: '#ffffff',
      }}
    >
      {/* 品牌面板 */}
      <Box
        sx={{
          flex: { md: '1 1 55%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 6,
          px: { xs: 3.5, md: 8 },
          py: { xs: 4, md: 6 },
          color: '#fff',
          background: 'linear-gradient(200deg, #123a6b 0%, #0a1f3d 70%)',
        }}
      >
        {/* 字標 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: '10px',
              bgcolor: '#1d63b8',
            }}
          >
            <SportsIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography sx={{ fontFamily: 'inherit', fontWeight: 700, fontSize: 16 }}>
            運動員查詢系統
          </Typography>
        </Box>

        {/* 主標語 */}
        <Box sx={{ my: { xs: 1, md: 0 } }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'inherit',
              fontWeight: 900,
              fontSize: { xs: '1.9rem', md: '3.1rem' },
              lineHeight: 1.25,
              letterSpacing: '0.01em',
              textWrap: 'balance',
              mb: 2,
            }}
          >
            看見全國賽場上
            <br />
            每一筆成績的價值
          </Typography>
          <Typography
            sx={{
              fontFamily: 'inherit',
              fontWeight: 400,
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              color: 'rgba(255,255,255,0.72)',
              maxWidth: '32em',
            }}
          >
            全國性賽事成績查詢、縣市與學校實力分析，一站完成。
          </Typography>
        </Box>

        {/* 排行榜意象 */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 420 }}>
          <Leaderboard />
          <Box
            sx={{
              borderTop: '1px solid rgba(255,255,255,0.25)',
              pt: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              color: 'rgba(255,255,255,0.55)',
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          >
            <span>縣市實力</span>
            <span>歷年趨勢</span>
            <span>名次分布</span>
          </Box>
        </Box>
      </Box>

      {/* 登入面板 */}
      <Box
        sx={{
          flex: { md: '1 1 45%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3.5,
          py: { xs: 6, md: 8 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: 'inherit',
              fontWeight: 700,
              fontSize: '1.5rem',
              color: '#122238',
              mb: 1,
            }}
          >
            登入系統
          </Typography>
          <Typography
            sx={{
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              color: '#43506b',
              mb: 4,
            }}
          >
            請使用已授權的 Google 帳號登入
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              minHeight: 44,
            }}
          >
            {loading && <CircularProgress size={26} />}
            {/* 這個 div 必須常駐 DOM，renderButton 才拿得到節點 */}
            <div ref={buttonRef} />
          </Box>

          <Typography
            sx={{
              fontFamily: 'inherit',
              fontSize: 13,
              color: '#5f6b84',
              mt: 4,
              pt: 3,
              borderTop: '1px solid #e6e9f0',
            }}
          >
            本系統僅限授權帳號使用，需要開通權限請聯絡系統管理員。
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default LoginPage;
